import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, ChevronDown, Undo2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useToday } from '@/hooks/useOrgSettings';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/dates';
import { Badge, Button, Card, CardContent, ErrorState, Input, Money, SelectField } from '@/features/common/ui';
import type { CfoSettings, DueView, DuesCalendar, TaxSettings } from './types';

const STATUS: Record<DueView['status'], { label: string; variant: 'danger' | 'warning' | 'neutral' | 'success' }> = {
  overdue: { label: 'Overdue', variant: 'danger' },
  soon: { label: 'Due soon', variant: 'warning' },
  later: { label: 'Coming', variant: 'neutral' },
  done: { label: 'Done', variant: 'success' },
};

/**
 * Dues: every statutory date for the book - GST, TDS, advance tax, the LLP's
 * forms, the ITR, the monthly export declaration - worked out from the tax
 * settings, with the penalty for missing it and what Fint estimates is
 * payable. A due is done when a person marks it, or for a GST return when
 * the period is locked as filed.
 */
export function DuesTab() {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const calendar = useQuery({ queryKey: ['cfo-dues', org?.id], queryFn: () => api.get<DuesCalendar>('/cfo/dues'), enabled: !!org?.id });
  const settings = useQuery({ queryKey: ['cfo-settings', org?.id], queryFn: () => api.get<CfoSettings>('/cfo/settings'), enabled: !!org?.id });
  const [showDone, setShowDone] = useState(false);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cfo-dues', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['cfo-settings', org?.id] });
  };

  if (calendar.isError) return <Card><ErrorState error={calendar.error} onRetry={() => calendar.refetch()} /></Card>;
  if (!calendar.data) return <div className="skeleton h-48 rounded-lg" />;
  const data = calendar.data;
  const open = data.dues.filter((d) => d.status !== 'done');
  const done = data.dues.filter((d) => d.status === 'done');

  return (
    <div className="space-y-6">
      {settings.data ? <TaxCard settings={settings.data} questions={data.questions} onSaved={refresh} /> : null}

      <section className="space-y-2" aria-labelledby="dues-open">
        <h2 id="dues-open" className="flex items-center gap-2 text-sm font-semibold">
          <CalendarClock className="size-4 text-muted-foreground" aria-hidden /> Due from {formatDate(data.from)} to {formatDate(data.to)}
        </h2>
        {open.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing open in this window.</p>
        ) : (
          <Card>
            <ul className="divide-y divide-border">
              {open.map((due) => (
                <DueRow key={due.key} due={due} currency={data.currency} canMark={data.canMark} onDone={refresh} />
              ))}
            </ul>
          </Card>
        )}
      </section>

      {done.length ? (
        <section className="space-y-2">
          <button type="button" className="inline-flex items-center gap-1 text-sm font-semibold touch:min-h-11" aria-expanded={showDone} onClick={() => setShowDone((v) => !v)}>
            Done ({done.length}) <ChevronDown className={cn('size-4 transition-transform', showDone && 'rotate-180')} aria-hidden />
          </button>
          {showDone ? (
            <Card>
              <ul className="divide-y divide-border">
                {done.map((due) => (
                  <DueRow key={due.key} due={due} currency={data.currency} canMark={data.canMark} onDone={refresh} />
                ))}
              </ul>
            </Card>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function DueRow({ due, currency, canMark, onDone }: { due: DueView; currency: string; canMark: boolean; onDone: () => void }) {
  const today = useToday();
  const [marking, setMarking] = useState(false);
  const [doneOn, setDoneOn] = useState(today);
  const [amount, setAmount] = useState(due.estimate ?? '');
  const [reference, setReference] = useState('');
  const [open, setOpen] = useState(false);
  const mark = useMutation({
    mutationFn: () => api.put(`/cfo/dues/${encodeURIComponent(due.key)}`, { doneOn, amount: amount.trim() || null, reference: reference.trim() || null }),
    onSuccess: () => {
      setMarking(false);
      onDone();
    },
  });
  const unmark = useMutation({ mutationFn: () => api.delete(`/cfo/dues/${encodeURIComponent(due.key)}`), onSuccess: onDone });
  const error = (mark.error ?? unmark.error) instanceof ApiError ? ((mark.error ?? unmark.error) as ApiError) : undefined;
  const status = STATUS[due.status];

  return (
    <li className="space-y-2 px-4 py-3 text-sm" data-due={due.key} data-due-status={due.status}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">
            {due.title} <span className="text-muted-foreground">· {due.period}</span>
            {due.optional ? <span className="text-xs text-muted-foreground"> (optional)</span> : null}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDate(due.date)} ·{' '}
            {due.status === 'done'
              ? due.done?.byLock
                ? 'filed: the period is locked'
                : `done ${formatDate(due.done?.doneOn)}${due.done?.reference ? `, ${due.done.reference}` : ''}`
              : due.daysLeft < 0
                ? `${-due.daysLeft} days ago`
                : due.daysLeft === 0
                  ? 'today'
                  : `in ${due.daysLeft} days`}
          </p>
          {due.note ? <p className="text-xs text-warning">{due.note}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          {due.estimate ? (
            <span className="text-xs text-muted-foreground">
              about <Money value={due.estimate} currency={currency} />
            </span>
          ) : null}
          {due.done?.amount ? <Money value={due.done.amount} currency={currency} className="text-xs" /> : null}
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline touch:min-h-11">
          Penalty and source <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
        {canMark && due.status !== 'done' && !marking ? (
          <Button size="sm" variant="outline" onClick={() => setMarking(true)} data-due-mark>
            <Check className="size-4" aria-hidden /> Mark done
          </Button>
        ) : null}
        {canMark && due.status === 'done' && !due.done?.byLock ? (
          <Button size="sm" variant="ghost" onClick={() => unmark.mutate()} loading={unmark.isPending}>
            <Undo2 className="size-4" aria-hidden /> Not done
          </Button>
        ) : null}
      </div>
      {open ? (
        <div className="space-y-1 rounded-md bg-muted/50 px-3 py-2 text-xs">
          <p>{due.penalty}</p>
          <p className="text-muted-foreground">{due.source}</p>
        </div>
      ) : null}
      {marking ? (
        <form
          className="grid gap-2 sm:grid-cols-[repeat(3,minmax(0,1fr))_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            mark.mutate();
          }}
        >
          <Input label="Filed or paid on" type="date" required value={doneOn} onChange={(e) => setDoneOn(e.target.value)} />
          <Input label="Amount paid" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} hint="Leave empty for a filing." />
          <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} hint="ARN, challan or SRN." />
          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={mark.isPending}>
              Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setMarking(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error.message}
        </p>
      ) : null}
    </li>
  );
}

const yesNo = (value: boolean | null) => (value === null ? '' : value ? 'yes' : 'no');
const fromYesNo = (value: string) => (value === '' ? null : value === 'yes');

function TaxCard({ settings, questions, onSaved }: { settings: CfoSettings; questions: string[]; onSaved: () => void }) {
  const [editing, setEditing] = useState(questions.length > 0 && !!settings.canEdit);
  const [tax, setTax] = useState<TaxSettings>(settings.tax);
  const save = useMutation({
    mutationFn: () => api.patch('/cfo/settings', { tax }),
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });
  const set = <K extends keyof TaxSettings>(key: K, value: TaxSettings[K]) => setTax((t) => ({ ...t, [key]: value }));
  const error = save.error instanceof ApiError ? save.error : undefined;

  return (
    <Card data-cfo-tax-settings>
      <CardContent className="space-y-3 pt-4 text-sm">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-semibold">Tax details</h2>
          {settings.canEdit && !editing ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Change
            </Button>
          ) : null}
        </div>
        {editing && questions.length ? (
          <p className="text-xs text-muted-foreground">The calendar is more exact with these: {questions.join(' ')}</p>
        ) : null}
        {!editing ? (
          <>
            <p className="text-muted-foreground">
              GST {settings.tax.gstFiling === 'qrmp' ? 'quarterly (QRMP)' : settings.tax.gstFiling === 'monthly' ? 'monthly' : 'frequency not set'} ·{' '}
              {settings.tax.entity ? settings.tax.entity.toUpperCase() : 'entity not set'}
              {settings.tax.audited !== null ? ` · ${settings.tax.audited ? 'audited' : 'not audited'}` : ''}
              {settings.tax.advanceTax ? ' · pays advance tax' : ''}
              {settings.tax.deductsTds ? ' · deducts TDS' : ''}
              {settings.tax.udyam ? ` · Udyam ${settings.tax.udyam} (${settings.tax.msmeCategory ?? 'category not set'})` : ''}
            </p>
            {questions.length ? (
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-warning">
                {questions.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          <form
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <SelectField id="tax-gst" label="GST returns" value={tax.gstFiling ?? ''} onChange={(v) => set('gstFiling', (v || null) as TaxSettings['gstFiling'])}>
              <option value="">Not set</option>
              <option value="monthly">Monthly</option>
              <option value="qrmp">Quarterly (QRMP)</option>
            </SelectField>
            <SelectField id="tax-entity" label="The business is" value={tax.entity ?? ''} onChange={(v) => set('entity', (v || null) as TaxSettings['entity'])}>
              <option value="">Not set</option>
              <option value="llp">An LLP</option>
              <option value="company">A company</option>
              <option value="partnership">A partnership firm</option>
              <option value="proprietor">A proprietorship</option>
              <option value="other">Something else</option>
            </SelectField>
            <SelectField id="tax-audited" label="Accounts audited" value={yesNo(tax.audited)} onChange={(v) => set('audited', fromYesNo(v))}>
              <option value="">Not sure</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </SelectField>
            <SelectField id="tax-advance" label="Pays advance tax" value={yesNo(tax.advanceTax)} onChange={(v) => set('advanceTax', fromYesNo(v))}>
              <option value="">Not sure</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </SelectField>
            <SelectField id="tax-tds" label="Deducts TDS on payments" value={yesNo(tax.deductsTds)} onChange={(v) => set('deductsTds', fromYesNo(v))}>
              <option value="">Not sure</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </SelectField>
            <Input label="Udyam number" value={tax.udyam ?? ''} onChange={(e) => set('udyam', e.target.value || null)} maxLength={40} hint="Lets a final reminder cite MSMED Act interest." />
            <SelectField id="tax-msme" label="MSME category" value={tax.msmeCategory ?? ''} onChange={(v) => set('msmeCategory', (v || null) as TaxSettings['msmeCategory'])}>
              <option value="">Not set</option>
              <option value="micro">Micro</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
            </SelectField>
            {error ? (
              <p role="alert" className="text-sm text-destructive sm:col-span-2">
                {error.message}
              </p>
            ) : null}
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" size="sm" loading={save.isPending} data-cfo-tax-save>
                Save
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
